/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */

import React from 'react'
import { useIntl } from 'react-intl'
import { Box, Typography, Card, CardContent, Button } from '@material-ui/core'
import makeStyles from '@material-ui/styles/makeStyles'

const unsupportedBrowserStyles = {
  container: {
    display: 'flex',
    height: '100vh',
    background: 'rgba(0, 0, 0, 0.38)',
    '& p': {
      fontFamily: "'Lato', 'LatoFallback', sans-serif",
    },
  },
  card: {
    borderRadius: 6,
    margin: 'auto',
    width: 400,
    height: 232,
    boxShadow: '0px 9px 46px 0px #0000001F, 0px 24px 38px 0px #00000024, 0px 11px 15px 0px #00000033',
  },
  header: {
    fontFamily: "'Lato', 'LatoFallback', sans-serif",
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '1em',
    lineHeight: '20px',
    textAlign: 'center',
    color: '#C42014',
  },
  body: {
    fontFamily: 'Lato',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '20px',
    textAlign: 'center',
    color: '#0D1A2D',
    marginTop: 8,
  },
  button: {
    background: '#007DBA',
    borderRadius: '6px',
    color: '#FFFFFF',
    height: 36,
    width: 204,
  },
  buttonText: {
    fontFamily: 'Lato',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
    textTransform: 'none',
  },
  content: {
    height: 164,
    padding: 32,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
    background: '#F3F5F7',
    height: '68px',
  },
}
/**
 * Testing requires this to be a function.
 * This unique implementation is required because lambda functions aren't supported by IE11
 * @returns {Object} a function that returns the unsupportedBrowser styles
 */
function getStyles() {
  return unsupportedBrowserStyles
}
const useStyles = makeStyles(getStyles)

/**
 * An unsupported browser error message that provides the user with supported browser options
 * @returns {Node} - A React node.
 */
function UnsupportedBrowser() {
  const classes = useStyles()
  const intl = useIntl()

  // prettier-ignore
  const messages = {
    titleText: intl.formatMessage({ id: 'unsupported_title', defaultMessage: 'Your browser is not supported' }),
    bodyText: intl.formatMessage({ id: 'unsupported_body', defaultMessage: 'Our website detected you are using an outdated browser that we do not support due to privacy and security reasons. Visit our web site to view a list of compatible browsers.' }),
    downloadText: intl.formatMessage({ id: 'unsupported_button', defaultMessage: 'View Compatible Browsers' }),
  }

  return (
    <Box className={classes.container}>
      <Card className={classes.card}>
        <CardContent className={classes.content}>
          <Typography gutterBottom className={classes.header} variant='h5' component='h2'>
            {messages.titleText}
          </Typography>
          <Typography className={classes.body} variant='body2' color='textSecondary' component='p'>
            {messages.bodyText}
          </Typography>
        </CardContent>
        <Box className={classes.actions}>
          <Button
            className={classes.button}
            variant='contained'
            color='primary'
            aria-label='View Compatible Browsers'
            data-testid='unsupported-browser-button'
            target='_blank'
            href='https://help.oclc.org/Librarian_Toolbox/Browser?sl=en'>
            <Typography className={classes.buttonText}>{messages.downloadText}</Typography>
          </Button>
        </Box>
      </Card>
    </Box>
  )
}

export default UnsupportedBrowser
